import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { removeMessage } from 'src/actions/savedSearchActions';
import savedSearchService from 'src/services/savedSearchService';
import { auditActionTypes as actionType } from 'src/constants/actionTypes';

import dispatchError from 'src/utils/dispatchError';

import auditService from 'src/services/auditService';
import { removeMessageAudit } from 'src/actions/auditActions';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

const SaveSearchDialog = ({
  open, setOpen, fields, audit
}) => {
  const dispatch = useDispatch();
  const repo = useSelector((state) => state.repository.selected);
  const user = useSelector((state) => state.account.user);
  const savedSearchError = useSelector((state) => state.savedSearch.error);
  const savedAuditError = useSelector((state) => state.audit.message);

  let errorEither;
  if (savedSearchError && savedSearchError !== '') {
    errorEither = savedSearchError;
  }
  if (savedAuditError && savedAuditError !== '') {
    errorEither = savedAuditError;
  }

  const [searchName, setSearchName] = useState('');

  const handleClose = () => {
    setOpen(false);
    setSearchName('');
    if (!audit) {
      dispatch(removeMessage());
    } else {
      dispatch(removeMessageAudit());
    }
  };

  const handleSubmit = async () => {
    if (!audit) {
      const data = {
        name: searchName,
        userId: user.id,
        query: {
          repositoryName: repo.name,
          repositoryId: repo.id,
          queryFields: fields,
          maxHits: 0
        }
      };

      try {
        const savedSearchRes = await savedSearchService.saveSearch(data);
        const payload = {
          ...savedSearchRes,
          query: data.query || []
        };
        dispatch({
          type: actionType.SAVE_SUCCESS,
          payload: {
            savedSearch: payload,
            successMsg: 'Your search was saved'
          }
        });
      } catch (error) {
        dispatchError(dispatch, error, actionType.FAILURE, 'Failed to save the search');
        errorEither = 'Failed to save the search';
      }
      if (errorEither === undefined || errorEither === '') {
        handleClose();
      }
    } else {
      const data = {
        name: searchName,
        userId: user.id,
        query: {
          queryFields: fields,
          maxHits: 0
        }
      };

      try {
        const savedAuditSearchRes = await auditService.saveAuditSearch(data);
        const payload = {
          ...savedAuditSearchRes,
          query: data.query || []
        };
        dispatch({
          type: actionType.SAVE_SUCCESS,
          payload: {
            savedAuditSearch: payload,
            successMsg: 'Your search was saved'
          }
        });
      } catch (error) {
        dispatchError(dispatch, error, actionType.FAILURE, 'Failed to save the search');
        errorEither = 'Failed to save the search';
      }

      if (errorEither === undefined || errorEither === '') {
        handleClose();
      }
    }
  };

  return (
    <Dialog
      fullWidth
      open={open}
      onClose={(handleClose)}
      maxWidth="xs"
    >
      <DialogTitle>
        <Box style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%'
        }}
        >
          <Typography style={{ fontWeight: 500, fontSize: 16 }}>
            Save your search
          </Typography>

          <IconButton
            size="small"
            aria-label="Close"
            onClick={() => handleClose()}
          >
            <CloseIcon style={{ color: '#29307E' }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          margin="dense"
          placeholder="Search Name"
          variant="outlined"
          value={searchName}
          onChange={(e) => {
            setSearchName(e.target.value);
            dispatch(removeMessage());
            dispatch(removeMessageAudit());
          }}
          inputProps={{ maxLength: 50 }}
          error={!!errorEither}
          helperText={errorEither || ''}
        />
        <Box my={3}>
          {
            fields.map((field) => (
              <Box my={1} style={{ display: 'flex', justifyContent: 'start' }} key={field.name}>
                <Typography
                  mr={1}
                  variant="body1"
                  style={{ fontSize: 14, fontWeight: 500, marginRight: 4 }}
                >
                  {field.name}
                  :
                </Typography>
                <Typography variant="body1" style={{ fontSize: 14, fontWeight: 400 }}>
                  {field.value !== ''
                    ? field.value
                    : field.comparator}
                </Typography>
              </Box>
            ))
          }
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Box m={1}>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => handleSubmit()}
            color="primary"
            variant="contained"
            disabled={!searchName}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

SaveSearchDialog.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  fields: PropTypes.array,
  audit: PropTypes.bool
};

export default SaveSearchDialog;
